import React, { useState, useEffect, useRef } from 'react';
import { paymentLogService } from '../services/api';
import { FiSearch, FiChevronLeft, FiChevronRight, FiEye } from 'react-icons/fi';
import './PaymentLogs.css';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;
const toList = (data) => (Array.isArray(data) ? data : (data?.results ?? []));

const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusBadgeClass = (status) => {
  const map = {
    initiated: 'initiated',
    order_created: 'order',
    payment_completed: 'success',
    webhook_received: 'webhook',
    failed: 'failed',
  };
  return map[status] || 'default';
};

const PaymentLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const debounceRef = useRef(null);
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const delay = (searchQuery || '').trim() ? SEARCH_DEBOUNCE_MS : 0;
    const timer = setTimeout(() => {
      setPage(1);
      runFetch(1);
    }, delay);
    debounceRef.current = timer;
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  useEffect(() => {
    if (isMountedRef.current) {
      runFetch(page);
    } else {
      isMountedRef.current = true;
    }
  }, [statusFilter, page]);

  const runFetch = async (pageNum = page) => {
    setLoading(true);
    try {
      const params = { page: pageNum };
      if (statusFilter) params.status = statusFilter;
      const q = (searchQuery || '').trim();
      if (q) params.search = q;
      const response = await paymentLogService.getAll(params);
      const data = response.data;
      setLogs(toList(data));
      setTotalCount(data.count ?? toList(data).length);
    } catch (error) {
      console.error('Error fetching payment logs:', error);
      setLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading payment logs...</div>;
  }

  return (
    <div className="payment-logs-page">
      <div className="page-header">
        <h1>Payment Logs</h1>
      </div>

      <div className="payment-logs-toolbar">
        <div className="search-wrap">
          <FiSearch className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search by transaction ID, mobile, email, Razorpay ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-wrap">
          <label htmlFor="status-filter" className="filter-label">Status</label>
          <select
            id="status-filter"
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="initiated">Initiated</option>
            <option value="order_created">Order created</option>
            <option value="payment_completed">Payment completed</option>
            <option value="webhook_received">Webhook received</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      <div className="payment-logs-table-wrap">
        <table className="payment-logs-table">
          <thead>
            <tr>
              <th>Transaction ID</th>
              <th>Booking ID</th>
              <th>Mobile</th>
              <th>Razorpay Order</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr>
                <td colSpan="6" className="td-empty">
                  {!searchQuery && !statusFilter
                    ? 'No payment logs yet.'
                    : 'No logs match your search or filter.'}
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id}>
                  <td className="td-txn-id">{log.internal_transaction_id}</td>
                  <td>{log.booking_id_display || '—'}</td>
                  <td>{log.customer_mobile_number || '—'}</td>
                  <td className="td-razorpay">{log.razorpay_order_id || '—'}</td>
                  <td>
                    <span className={`badge badge-${statusBadgeClass(log.status)}`}>
                      {log.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-view-details"
                      onClick={async (e) => {
                        e.stopPropagation();
                        setShowModal(true);
                        setSelectedLog(log);
                        setModalLoading(true);
                        try {
                          const res = await paymentLogService.getById(log.id);
                          setSelectedLog(res.data);
                        } catch (err) {
                          console.error('Failed to fetch payment log details:', err);
                        } finally {
                          setModalLoading(false);
                        }
                      }}
                    >
                      <FiEye /> View details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="payment-logs-pagination">
          <span className="pagination-info">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
          </span>
          <div className="pagination-buttons">
            <button
              type="button"
              className="pagination-btn"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <FiChevronLeft /> Previous
            </button>
            <button
              type="button"
              className="pagination-btn"
              disabled={page * PAGE_SIZE >= totalCount || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next <FiChevronRight />
            </button>
          </div>
        </div>
      )}

      {/* Modal with payment log details */}
      {showModal && selectedLog && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowModal(false);
            setSelectedLog(null);
          }}
        >
          <div className="modal-content payment-log-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Payment Log Details</h2>
            {modalLoading ? (
              <div className="modal-loading">Loading details...</div>
            ) : (
            <div className="payment-log-form">
              <div className="form-group">
                <label>Transaction ID</label>
                <input type="text" value={selectedLog.internal_transaction_id || '—'} readOnly />
              </div>
              <div className="form-group">
                <label>Booking ID</label>
                <input type="text" value={selectedLog.booking_id_display || '—'} readOnly />
              </div>
              <div className="form-group">
                <label>Mobile</label>
                <input type="text" value={selectedLog.customer_mobile_number || '—'} readOnly />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="text" value={selectedLog.customer_email || '—'} readOnly />
              </div>
              <div className="form-group">
                <label>Status</label>
                <input type="text" value={selectedLog.status || '—'} readOnly />
              </div>
              <div className="form-group">
                <label>Razorpay Order ID</label>
                <input type="text" value={selectedLog.razorpay_order_id || '—'} readOnly />
              </div>
              <div className="form-group">
                <label>Razorpay Payment ID</label>
                <input type="text" value={selectedLog.razorpay_payment_id || '—'} readOnly />
              </div>
              <div className="form-group">
                <label>Created</label>
                <input type="text" value={formatDate(selectedLog.created_at)} readOnly />
              </div>
              <div className="form-group">
                <label>Updated</label>
                <input type="text" value={formatDate(selectedLog.updated_at)} readOnly />
              </div>
              {/* 1. Order request payload + requested time */}
              <div className="form-group form-group-full payment-flow-section">
                <label>1. Order Request (sent to Razorpay)</label>
                <div className="payload-meta">
                  {selectedLog.order_requested_at ? `Requested at: ${formatDate(selectedLog.order_requested_at)}` : ''}
                </div>
                <textarea
                  value={
                    Object.keys(selectedLog.order_request_payload || {}).length > 0
                      ? JSON.stringify(selectedLog.order_request_payload, null, 2)
                      : ''
                  }
                  readOnly
                  rows={6}
                  className={Object.keys(selectedLog.order_request_payload || {}).length > 0 ? '' : 'payload-empty'}
                />
              </div>
              {/* 2. Order response payload + responded time */}
              <div className="form-group form-group-full payment-flow-section">
                <label>2. Order Response (from Razorpay)</label>
                <div className="payload-meta">
                  {selectedLog.order_responded_at ? `Responded at: ${formatDate(selectedLog.order_responded_at)}` : ''}
                </div>
                <textarea
                  value={(() => {
                    const p = selectedLog.order_response_payload || {};
                    const legacy =
                      selectedLog.payload?.create_order_response || selectedLog.payload?.callback_response;
                    const data = Object.keys(p).length > 0 ? p : (legacy || {});
                    return Object.keys(data).length > 0 ? JSON.stringify(data, null, 2) : '';
                  })()}
                  readOnly
                  rows={8}
                  className={
                    Object.keys(selectedLog.order_response_payload || {}).length > 0 ||
                    selectedLog.payload?.create_order_response ||
                    selectedLog.payload?.callback_response
                      ? ''
                      : 'payload-empty'
                  }
                />
              </div>
              {/* 3. Webhook response payload + responded time */}
              <div className="form-group form-group-full payment-flow-section">
                <label>3. Webhook Response (from Razorpay)</label>
                <div className="payload-meta">
                  {selectedLog.webhook_responded_at ? `Received at: ${formatDate(selectedLog.webhook_responded_at)}` : ''}
                </div>
                <textarea
                  value={(() => {
                    const p = selectedLog.webhook_response_payload || {};
                    const legacy = selectedLog.payload?.webhook;
                    const data = Object.keys(p).length > 0 ? p : (legacy || {});
                    return Object.keys(data).length > 0 ? JSON.stringify(data, null, 2) : '';
                  })()}
                  readOnly
                  rows={10}
                  className={
                    Object.keys(selectedLog.webhook_response_payload || {}).length > 0 || selectedLog.payload?.webhook
                      ? ''
                      : 'payload-empty'
                  }
                />
              </div>

            </div>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowModal(false);
                  setSelectedLog(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentLogs;
